const number = (value) => {
  if (value == null || String(value).trim() === "") return null;
  const result = Number(value);
  return Number.isFinite(result) ? result : null;
};

export const drawingCoordinateFields = (overlay) => {
  if (overlay?.kind === "horizontal_line")
    return [
      { key: "price", label: "Price", value: overlay.price ?? overlay.value },
    ];
  if (["price_zone", "price_zone_box"].includes(overlay?.kind))
    return [
      {
        key: "bottom",
        label: "Lower price",
        value: overlay.bottom ?? overlay.lower,
      },
      { key: "top", label: "Upper price", value: overlay.top ?? overlay.upper },
    ];
  if (
    ["trend_line", "rectangle", "circle", "parabola", "semicircle"].includes(
      overlay?.kind,
    )
  ) {
    return ["start", "end"].flatMap((endpoint) => {
      const anchor = overlay[`${endpoint}_anchor`] || {};
      const label = endpoint === "start" ? "Start" : "End";
      return [
        {
          key: `${endpoint}_price`,
          label: `${label} price`,
          value: anchor.price,
        },
        {
          key: `${endpoint}_time`,
          label: `${label} time (UTC)`,
          type: "datetime-local",
          value: Number.isFinite(Number(anchor.time))
            ? new Date(Number(anchor.time) * 1000).toISOString().slice(0, 19)
            : "",
        },
      ];
    });
  }
  return [];
};

export const drawingCoordinatePatch = (overlay, values, tickSize) => {
  if (overlay?.locked)
    return { error: "Unlock the drawing to edit coordinates." };
  const fields = drawingCoordinateFields(overlay);
  if (!fields.length) return { error: "This drawing uses a freehand path." };
  const parsed = {};
  for (const field of fields) {
    const raw = values[field.key];
    const value =
      field.type === "datetime-local"
        ? /^\d{4}-\d\d-\d\dT\d\d:\d\d(?::\d\d)?$/.test(raw || "")
          ? Date.parse(`${raw}Z`) / 1000
          : null
        : number(raw);
    if (value == null || !Number.isFinite(value))
      return { error: `Enter a valid ${field.label.toLowerCase()}.` };
    const tick = number(tickSize);
    // Untouched market anchors may predate a tick-rule change. Never silently round them.
    if (!field.type && tick > 0 && value !== number(field.value)) {
      const units = value / tick;
      if (
        Math.abs(units - Math.round(units)) >
        Math.max(1, Math.abs(units)) * Number.EPSILON * 8
      )
        return { error: `Use price increments of ${tick}.` };
    }
    parsed[field.key] = value;
  }
  if (overlay.kind === "horizontal_line")
    return { patch: { price: parsed.price } };
  if (["price_zone", "price_zone_box"].includes(overlay.kind)) {
    if (parsed.bottom >= parsed.top)
      return { error: "Upper price must be greater than lower price." };
    return { patch: { bottom: parsed.bottom, top: parsed.top } };
  }
  const patch = {};
  for (const endpoint of ["start", "end"]) {
    const old = overlay[`${endpoint}_anchor`];
    const time = parsed[`${endpoint}_time`];
    // A time edit replaces projected-bar offsets; a price edit preserves them.
    patch[`${endpoint}_anchor`] = {
      ...(time === Number(old?.time) ? old : {}),
      time,
      price: parsed[`${endpoint}_price`],
    };
  }
  if (
    patch.start_anchor.time === patch.end_anchor.time &&
    patch.start_anchor.price === patch.end_anchor.price
  )
    return { error: "The two endpoints must be different." };
  return { patch };
};

export const drawingPriceSummary = (overlay) => {
  const price = (value) => {
    const parsed = number(value);
    return parsed == null
      ? "—"
      : parsed.toLocaleString(undefined, { maximumFractionDigits: 20 });
  };
  if (
    overlay?.kind === "horizontal_line" ||
    overlay?.drawing_kind === "horizontal_line"
  )
    return price(overlay.price ?? overlay.value);
  if (
    ["price_zone", "price_zone_box"].includes(
      overlay?.kind || overlay?.drawing_kind,
    )
  )
    return `${price(overlay.bottom)} – ${price(overlay.top)}`;
  if (overlay?.start_anchor && overlay?.end_anchor)
    return `${price(overlay.start_anchor.price)} → ${price(overlay.end_anchor.price)}`;
  return String(overlay?.kind || "Drawing").replaceAll("_", " ");
};
